/**
 * The reader's view of a khutbah, and the parser that proves a response is
 * actually that shape.
 *
 * TypeScript validates nothing at runtime, so `response.json() as KhutbahReading`
 * is a lie that compiles — and the lie surfaces as a blank screen or a thrown
 * `undefined` three components deep, nowhere near the cause. Everything here
 * narrows from `unknown` at the boundary instead.
 */

export type PassageType =
  | 'prose'
  | 'quran'
  | 'hadith'
  | 'supplication'
  | 'heading'
  | 'poetry';

export type SectionType =
  | 'opening'
  | 'first_khutbah'
  | 'second_khutbah'
  | 'supplication'
  | 'footnotes'
  | 'other';

export type TranslationOrigin = 'ai' | 'publisher' | 'human';
export type EntryMethod = 'api' | 'manual' | 'import';

export interface TranslationReading {
  readonly text: string;
  readonly origin: TranslationOrigin;
  readonly entryMethod: EntryMethod;
  readonly status: string;
  /** A human approved it for publication. */
  readonly reviewed: boolean;
  /**
   * The faithfulness check has run. `false` means nobody has looked — which is
   * not the same as nothing being wrong, and is why the badge for it says
   * "unreviewed" in words rather than relying on a colour.
   */
  readonly checked: boolean;
  readonly modelId: string | null;
  /** For a manual paste: the model the editor says produced it. A claim. */
  readonly declaredModel: string | null;
}

export interface PassageReading {
  readonly passageId: string;
  readonly ordinal: number;
  readonly passageType: PassageType;
  readonly sectionType: SectionType;
  readonly sourceText: string;
  readonly pageNo: number | null;
  readonly sourceUncertain: boolean;
  /**
   * `null` where nothing has been translated yet — deliberately null rather
   * than an empty string, because "not translated" and "translated as nothing"
   * must not render the same way.
   */
  readonly translation: TranslationReading | null;
}

export interface KhutbahReading {
  readonly khutbahId: string;
  /** Belongs in every query key; see `readingQueryKey`. */
  readonly sourceVersionId: string | null;
  readonly institutionKey: string;
  readonly institutionName: string;
  readonly sourceVerified: boolean;
  readonly intendedDate: string;
  readonly titleOriginal: string | null;
  readonly languageCode: string;
  readonly languageName: string;
  readonly direction: 'ltr' | 'rtl';
  /** The `.script-*` class this language's text must carry. */
  readonly scriptClass: string;
  readonly fontToken: string;
  readonly passages: readonly PassageReading[];
  /**
   * The language the khutbah itself is in, and how to draw it.
   *
   * From the server, not assumed. The source pane was hardcoded to Arabic
   * RTL, which is right today and would have been silently wrong the moment
   * a khutbah arrived in another language — and a wrong direction is not a
   * subtle defect, it is an unreadable pane.
   */
  readonly sourceLanguageCode: string;
  readonly sourceDirection: 'ltr' | 'rtl';
  readonly sourceScriptClass: string;
}

/**
 * Does this passage carry actual words?
 *
 * The extractor emits structural artifacts as passages: an empty opening
 * marker, and the `*** *** ***` rule between the two khutbahs. They have no
 * words to translate, so counting them as untranslated is simply wrong — it
 * produced a header reading "24 of 23 passages" — and rendering "Not
 * translated yet" against them reports the product as broken when the truth
 * is that a divider is a divider.
 */
export function hasWords(passage: PassageReading): boolean {
  return /\p{L}/u.test(passage.sourceText);
}

/**
 * Should this passage be rendered at all?
 *
 * <p>Three reasons to keep one: it has words, it has a translation, or it is
 * flagged `sourceUncertain`.
 *
 * <p>That third clause is the one that matters. Extraction deliberately
 * preserves a passage whose text could not be read — a quotation whose glyph
 * codes were stripped — so that a reader is told something is missing. Without
 * it the filter hid exactly that evidence: such a passage has no words and no
 * translation, so it looked like a structural artifact and vanished, which is
 * the silent loss extraction was changed to prevent. A passage is dropped only
 * when it carries nothing and claims nothing.
 */
export function isRenderable(passage: PassageReading): boolean {
  return hasWords(passage) || passage.translation !== null || passage.sourceUncertain;
}

/** Thrown when a response does not match the contract. Never silently coerced. */
export class ResponseShapeError extends Error {
  constructor(what: string) {
    super(`The server sent something this version cannot read: ${what}`);
    this.name = 'ResponseShapeError';
  }
}

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ResponseShapeError(`${where} is not an object`);
  }
  return value as Record<string, unknown>;
}

function str(source: Record<string, unknown>, key: string, where: string): string {
  const value = source[key];
  if (typeof value !== 'string') {
    throw new ResponseShapeError(`${where}.${key} is not a string`);
  }
  return value;
}

function strOrNull(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' ? value : null;
}

function bool(source: Record<string, unknown>, key: string, where: string): boolean {
  const value = source[key];
  if (typeof value !== 'boolean') {
    throw new ResponseShapeError(`${where}.${key} is not a boolean`);
  }
  return value;
}

function num(source: Record<string, unknown>, key: string, where: string): number {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new ResponseShapeError(`${where}.${key} is not a number`);
  }
  return value;
}

function numOrNull(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Widened rather than validated against the union, on purpose.
 *
 * A passage type or origin the server adds later must not break reading an
 * otherwise perfectly good khutbah — the renderer falls back to prose styling
 * for a type it does not know. Validating this strictly would mean a backend
 * deployment could blank the reader for everyone on an older bundle.
 */
function enumish<T extends string>(value: unknown, fallback: T): T {
  return typeof value === 'string' ? (value as T) : fallback;
}

function parseTranslation(value: unknown, where: string): TranslationReading | null {
  if (value === null || value === undefined) {
    return null;
  }
  const source = asRecord(value, where);
  return {
    text: str(source, 'text', where),
    origin: enumish<TranslationOrigin>(source.origin, 'ai'),
    entryMethod: enumish<EntryMethod>(source.entryMethod, 'api'),
    status: strOrNull(source, 'status') ?? 'ai_draft',
    reviewed: bool(source, 'reviewed', where),
    checked: bool(source, 'checked', where),
    modelId: strOrNull(source, 'modelId'),
    declaredModel: strOrNull(source, 'declaredModel'),
  };
}

function parsePassage(value: unknown, index: number): PassageReading {
  const where = `passages[${index}]`;
  const source = asRecord(value, where);
  return {
    passageId: str(source, 'passageId', where),
    ordinal: num(source, 'ordinal', where),
    passageType: enumish<PassageType>(source.passageType, 'prose'),
    sectionType: enumish<SectionType>(source.sectionType, 'other'),
    // A Qur'an passage legitimately has no text until the glyph map decodes it
    // (ADR 0007), so an empty string is valid content here, not a missing field.
    sourceText: strOrNull(source, 'sourceText') ?? '',
    pageNo: numOrNull(source, 'pageNo'),
    sourceUncertain: source.sourceUncertain === true,
    translation: parseTranslation(source.translation, `${where}.translation`),
  };
}

export function parseKhutbahReading(value: unknown): KhutbahReading {
  const source = asRecord(value, 'response');
  const passages = source.passages;
  if (!Array.isArray(passages)) {
    throw new ResponseShapeError('response.passages is not an array');
  }

  const direction = source.direction === 'rtl' ? 'rtl' : 'ltr';

  return {
    khutbahId: str(source, 'khutbahId', 'response'),
    sourceVersionId: strOrNull(source, 'sourceVersionId'),
    institutionKey: str(source, 'institutionKey', 'response'),
    institutionName: str(source, 'institutionName', 'response'),
    sourceVerified: bool(source, 'sourceVerified', 'response'),
    intendedDate: str(source, 'intendedDate', 'response'),
    titleOriginal: strOrNull(source, 'titleOriginal'),
    languageCode: str(source, 'languageCode', 'response'),
    languageName: str(source, 'languageName', 'response'),
    direction,
    scriptClass: strOrNull(source, 'scriptClass') ?? 'script-latin',
    fontToken: strOrNull(source, 'fontToken') ?? 'latin',
    sourceLanguageCode: strOrNull(source, 'sourceLanguageCode') ?? 'ar',
    // Arabic's direction is the fallback, not 'ltr': if the server did not
    // say, every khutbah in the system today is Arabic, and guessing ltr
    // would render it backwards rather than merely imperfectly.
    sourceDirection: source.sourceDirection === 'ltr' ? 'ltr' : 'rtl',
    sourceScriptClass: strOrNull(source, 'sourceScriptClass') ?? 'script-ar',
    passages: passages.map(parsePassage),
  };
}

export function parseLanguageList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    throw new ResponseShapeError('language list is not an array');
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * The cache key for a reading.
 *
 * `sourceVersionId` is in it because the frontend standards require it: a key
 * of khutbah + language alone keeps serving the previous extraction's passages
 * after a new one is ingested. It is included even though the caller does not
 * know it in advance — the first fetch keys without it, and once a reading is
 * in hand the version-qualified key is what everything else uses.
 */
export function readingQueryKey(
  khutbahId: string,
  language: string,
  sourceVersionId?: string | null,
) {
  return ['khutbah', khutbahId, 'reading', language, sourceVersionId ?? 'current'] as const;
}
